import React from 'react';
import PropTypes from 'prop-types';
import { Table, Modal } from 'antd';
import { Helmet } from 'react-helmet';
import { get, pickBy } from 'lodash';
import { translate } from 'react-i18next';
import i18next from 'i18next';
import moment from 'moment';

import FundkyButton from 'Parts/ui/components/FundkyButton';
import Can from 'Parts/session/components/CanContainer';
import TextAreaField from 'Parts/ui/components/fields/TextAreaField';

import { simpleColumn, dateColumn } from 'Parts/common/columnUtils';

import TaxReceiptPanel_en from './locales/TaxReceiptPanel_en.json';
import TaxReceiptPanel_fr from './locales/TaxReceiptPanel_fr.json';

import './TaxReceiptPanel.less';

class TaxReceiptPanel extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      displayActionModal: false,
      displayIssueModal: false,
      actionModal: '',
      valid: this.isOfficialStatusValid(
        get(props, 'transaction.plugins.taxReceipt[0].statusId', null),
        get(props, 'transaction.plugins.taxReceipt[0].addedOn', null)
      ),
      // Subscription type (6) auto-send receipt at the end of the fiscal year
      disableButtons: get(props, 'transaction.typeId') === 6
    };

    if (props.transaction.id) {
      this.props.fetchTaxReceiptLogs(props.transaction.id);
      this.props.fetchTaxReceiptConfiguration();
    }

    i18next.addResourceBundle('en', 'TaxReceiptPanel', TaxReceiptPanel_en);
    i18next.addResourceBundle('fr', 'TaxReceiptPanel', TaxReceiptPanel_fr);
  }

  componentDidUpdate(prevProps) {
    const valid = this.isOfficialStatusValid(
      get(this.props, 'transaction.plugins.taxReceipt[0].statusId', null),
      get(this.props, 'transaction.plugins.taxReceipt[0].addedOn', null)
    );

    if (this.state.valid !== valid) {
      this.setState({
        valid: valid
      });
    }

    if(get(prevProps, 'transaction.typeId') !== get(this.props, 'transaction.typeId')) {
      this.setState({
        disableButtons: get(this.props, 'transaction.typeId') === 6
      });
    }
  }

  showActionModal = (event, action, t) => {
    this.setState({
      displayActionModal: true,
      actionModal: action
    });
  };

  showIssueModal = (event, action, t) => {
    this.setState({
      displayIssueModal: true,
      actionModal: action
    });
  };

  handleModalOk = event => {
    const transactionId = this.props.transaction.id;
    const action = this.state.actionModal;
    const { validateFields, isFieldTouched } = this.props.form;

    // The force option is required because we have dynamic rules
    // See https://github.com/react-component/form/issues/130
    validateFields({ force: true }, (err, allValues) => {
      if (!err) {
        const updatedValues = pickBy(allValues, (value, key) => isFieldTouched(key));

        if (action === 'cancel') {
          this.props.cancelTaxReceipt(event, transactionId, updatedValues.message, this.props.t);
        } else if (action === 'regenerate') {
          this.props.regenerateTaxReceipt(
            event,
            transactionId,
            updatedValues.message,
            this.props.t
          );
        } else if (action === 'issue') {
          this.props.generateTaxReceipt(event, transactionId, this.props.t);
        }

        this.hideActionModal();
      }
    });
  };

  hideActionModal = () => {
    this.setState(
      {
        displayIssueModal: false,
        displayActionModal: false
      },
      () => {
        if (this.state.actionModal !== 'issue')
          this.TextAreaField.getWrappedInstance().resetTextAreaField();
      }
    );
  };

  hideIssueModal = () => {
    this.setState({
      displayIssueModal: false
    });
  };

  getEvent = entry => {
    const { t } = this.props;

    switch (entry.event) {
      case 'FK-PLUGINS-TAX-RECEIPT-DOWNLOAD':
        return t('downloaded');
      case 'FK-PLUGINS-TAX-RECEIPT-CANCEL':
        return t('cancelled');
      case 'FK-PLUGINS-TAX-RECEIPT-CREATE':
        return t('created');
      case 'FK-PLUGINS-TAX-RECEIPT-REGENERATE':
        return t('regenerated');
      case 'FK-PLUGINS-TAX-RECEIPT-SEND':
        return t('sent');
      default:
        return null;
    }
  };

  isOfficialStatusValid = (statusId, addedOn) => {
    // 1 UNPROCESSED
    // 2 PROCESSING
    // 3 CREATED
    // 4 CANCELED
    const diff = moment().diff(addedOn);
    const delay = 10 * 60 * 1000; // 10 minutes in milliseconds

    // TODO: Ask what to do with status 1-2 AGAIN.
    return statusId === 3
      ? 3
      : statusId === 2
        ? diff < delay
          ? 2
          : 1
        : false;
  };

  render() {
    const { t, dispatch, transaction } = this.props;

    const taxReceiptId = get(transaction, 'plugins.taxReceipt[0].id', null);
    const taxReceiptNumber = get(transaction, 'plugins.taxReceipt[0].receiptNumber', null);

    const actionModal = true ? (
      <Modal
        className="TaxReceipt__Panel--Modal"
        visible={this.state.displayActionModal
          ? true
          : false
        }
        footer={null}
        onCancel={this.handleModalCancel}
        maskClosable
      >
        <FundkyButton
          className="TaxReceipt__Panel--closeModal"
          type="text"
          icon={['fas', 'times']}
          size="large"
          action={{ func: this.hideActionModal }}
        />
        <h1>{t(this.state.actionModal)}</h1>
        <TextAreaField
          className=""
          form={this.props.form}
          required={true}
          requiredMessage={t('reason-required')}
          label={t('reason')}
          placeholder={false}
          maxlength={144}
          ref={child => {
            this.TextAreaField = child;
          }}
        />
        <div className="TaxReceipt__Panel--buttons">
          <FundkyButton
            className=""
            type="cancel"
            text={t('cancel')}
            size="large"
            action={{ func: this.hideActionModal }}
          />
          <FundkyButton
            className=""
            type="primary"
            text={`${t(`${this.state.actionModal}-action`)}`}
            size="large"
            loading={this.props.isCancelInProgress || this.props.isRegenerateInProgress}
            action={{ func: this.handleModalOk }}
          />
        </div>
      </Modal>
    ) : null;

    const issueModal = (
      <Modal
        className="TaxReceipt__Panel--Modal"
        visible={this.state.displayIssueModal ? true : false}
        footer={null}
        onCancel={this.handleModalCancel}
        maskClosable
      >
        <FundkyButton
          className="TaxReceipt__Panel--closeModal"
          type="text"
          icon={['fas', 'times']}
          size="large"
          action={{ func: this.hideActionModal }}
        />
        <h1>{t(this.state.actionModal)}</h1>
        <p>{t('issue-message')}</p>
        <div className="TaxReceipt__Panel--buttons">
          <FundkyButton
            className=""
            type="cancel"
            text={t('cancel')}
            size="large"
            action={{ func: this.hideActionModal }}
          />
          <FundkyButton
            className=""
            type="primary"
            text={`${t('confirm')}`}
            size="large"
            loading={this.props.isCancelInProgress || this.props.isRegenerateInProgress}
            action={{ func: this.handleModalOk }}
          />
        </div>
      </Modal>
    );

    const typeMessage = get(this, 'props.transaction.donation.typeId') === 3
      ? this.props.t('type-message')
      : null;

    const disableIssueBtn = get(this, 'props.transaction.statusId') !== 1
      ? true
      : false;

    return (
      <div className="TaxReceipt__Panel ant-form-horizontal">
        <Helmet>
          <title>{t('page-title')}</title>
        </Helmet>
        <div className="TaxReceipt__Divider">
          <h3 className="panel-title">{t('information')}</h3>
          <span className="wrap-field">
            {typeMessage
              ? typeMessage
              : !this.state.disableButtons && (
                <>
                  <div className="TaxReceipt__Accepted ant-row ant-form-item">
                    <div className="ant-form-item-label">
                      <label className="TaxReceipt__Accepted--label">{t('donation-receipt-wanted')}</label>
                    </div>
                    <div className="ant-form-item-control-wrapper">
                      <div className="ant-form-item-control">
                        <span className="ant-form-item-children">
                          {taxReceiptId ? t('yes') : t('no')}
                        </span>
                      </div>
                    </div>
                  </div>
                  {(!taxReceiptId && !typeMessage) &&
                  <div className="TaxReceipt__Actions ant-row ant-form-item">
                    <div className="ant-form-item-label">
                      <label className="TaxReceipt__Actions--label">{t('options')}</label>
                    </div>
                    <div className="ant-form-item-control-wrapper">
                      <div className="ant-form-item-control">
                          <span className="TaxReceipt__Panel__Number--actions">
                            <FundkyButton
                              className="TaxReceipt__Panel__Generate"
                              disabled={disableIssueBtn || this.state.disableButtons}
                              type={"secondary"}
                              text={t('issue')}
                              icon={['fal', 'layer-plus']}
                              loading={this.props.isGenerateInProgress || this.props.isRegenerateInProgress}
                              action={{
                                func: this.showIssueModal,
                                params: ['issue', t]
                              }}
                            />
                          </span>
                      </div>
                    </div>
                  </div>
                  }
                </>
              ) || null
            }
          </span>
          {(taxReceiptId && !typeMessage && !this.state.disableButtons) && (
          <span className="wrap-field">
            <div className="TaxReceipt__Panel__Number ant-row ant-form-item">
              <div className="ant-form-item-label">
                <label className="TaxReceipt__Panel__Number-label">
                  {taxReceiptNumber
                    ? t('donation-receipt-number')
                    : t('options')
                  }
                </label>
              </div>
              <div className="ant-form-item-control-wrapper">
                <div className="ant-form-item-control">
                  <span className="ant-form-item-children TaxReceipt__Panel__Number--value">
                    {taxReceiptNumber
                      ? <FundkyButton
                          className="TaxReceipt__Panel__Download"
                          disabled={disableIssueBtn || this.state.valid === 2 || this.state.valid === 1 || this.state.disableButtons}
                          type={'text'}
                          text={taxReceiptNumber}
                          icon={['fal', 'external-link']}
                          loading={
                            this.props.isDownloadInProgress ||
                            this.props.isRegenerateInProgress ||
                            this.props.isCancelInProgress
                          }
                          action={{
                            func: this.props.downloadTaxReceipt,
                            params: [this.props.transaction.id, taxReceiptId, taxReceiptNumber, t]
                          }}
                        />
                      : <FundkyButton
                          className="TaxReceipt__Panel__Generate"
                          type={"secondary"}
                          disabled={disableIssueBtn || this.state.disableButtons}
                          text={t('generate')}
                          icon={['fal', 'layer-plus']}
                          loading={this.props.isGenerateInProgress || this.props.isRegenerateInProgress}
                          action={{
                            func: this.props.generateTaxReceipt,
                            params: [this.props.transaction.id, t]
                          }}
                        />
                    }
                  </span>
                </div>
              </div>
            </div>

            {(taxReceiptNumber) &&
            <div className="TaxReceipt__Panel__Actions ant-row ant-form-item">
              <div className="ant-form-item-label">
                <label className="TaxReceipt__Panel__Actions-label">{t('options')}</label>
              </div>
              <div className="ant-form-item-control-wrapper">
                <div className="ant-form-item-control">
                  <span className="ant-form-item-children TaxReceipt__Panel__Number--value">
                    <span className="TaxReceipt__Panel__Number--actions">
                      {(this.state.valid === 3 || this.state.valid === 2) &&
                        <FundkyButton
                          className="TaxReceipt__Panel__Resend"
                          disabled={disableIssueBtn || this.state.valid === 2 || this.state.disableButtons}
                          type={!disableIssueBtn
                            ? "secondary"
                            : "disabled"
                          }
                          text={t('resend')}
                          icon={['fal', 'share-square']}
                          loading={
                            this.props.isResendInProgress ||
                            this.props.isRegenerateInProgress ||
                            this.props.isCancelInProgress
                          }
                          action={{
                            func: this.props.resendTaxReceipt,
                            params: [this.props.transaction.id, taxReceiptId, t]
                          }}
                        />
                      }
                      {this.state.valid === 3 || this.state.valid === 2 || this.props.isRegenerateInProgress ? (
                        <FundkyButton
                          className="TaxReceipt__Panel__Regenerate"
                          disabled={disableIssueBtn || this.state.valid === 2 || this.state.disableButtons}
                          type={!disableIssueBtn
                            ? "secondary"
                            : "disabled"
                          }
                          text={t('regenerate')}
                          icon={['fal', 'layer-plus']}
                          loading={
                            this.props.isRegenerateInProgress ||
                            this.props.isCancelInProgress
                          }
                          action={{
                            func: this.showActionModal,
                            params: ['regenerate', t]
                          }}
                        />
                      ) : (
                        <FundkyButton
                          className="TaxReceipt__Panel__Generate"
                          disabled={disableIssueBtn || this.state.valid === 2 || this.state.disableButtons}
                          type={!disableIssueBtn
                            ? "secondary"
                            : "disabled"
                          }
                          text={t('generate')}
                          icon={['fal', 'layer-plus']}
                          loading={
                            this.props.isGenerateInProgress ||
                            this.props.isRegenerateInProgress
                          }
                          action={{
                            func: this.props.generateTaxReceipt,
                            params: [this.props.transaction.id, t]
                          }}
                        />
                      )}
                      <FundkyButton
                        className="TaxReceipt__Panel__Cancel"
                        type={
                          this.state.valid === 3 ||
                          this.props.isRegenerateInProgress
                            ? "primary"
                            : "disabled"
                        }
                        text={t('cancel')}
                        icon={['fal', 'ban']}
                        loading={
                          this.props.isCancelInProgress ||
                          this.props.isRegenerateInProgress
                        }
                        action={{
                          func: this.showActionModal,
                          params: ['cancel', t]
                        }}
                      />
                    </span>
                    {this.state.valid === 2 &&
                      <span>{t('message.processing')}
                        <FundkyButton
                          className="TaxReceipt__Panel__Refresh"
                          type='text'
                          text={t('message.refresh')}
                          action={{ func: () => window.location.reload() }}
                        />
                      </span>
                    }
                    {this.state.valid === 1 &&
                      <span>{t('message.processingFailled')}</span>
                    }
                  </span>
                </div>
              </div>
            </div>
            }
          </span>
          )}
          {this.state.disableButtons &&
          <p>{t('subscription-receipt')}</p>
          }
        </div>

        <Can key={taxReceiptId} rules={[
          'permissions.platform.plugin.taxReceipt.logs',
          () => taxReceiptId && !typeMessage
            ? true
            : false
        ]}>
          <div className="TaxReceipt__Divider">
            <h3 className="panel-title">{t('history')}</h3>
            <Table
              showHeader={true}
              size="small"
              rowKey={logEntry => logEntry._id}
              dataSource={(this.props.logs && this.props.logs.results) || null}
              locale={{ emptyText: t('no data') }}
              pagination={false}
              columns={[
                dateColumn(t('date'), 'addedOn', null, 'LLL'),
                simpleColumn(t('number'), null, logEntry => {
                  return (
                    <div className="block">
                      <span>{logEntry.log.receiptNumber}</span>
                      {logEntry.comment && <span>{`${t('reason')} ${logEntry.comment}`}</span>}
                    </div>
                  );
                }),
                simpleColumn(t('status'), null, logEntry => {
                  return <span>{this.getEvent(logEntry)}</span>;
                })
              ]}
            />
          </div>
        </Can>
        {actionModal}
        {issueModal}
      </div>
    );
  }
}

TaxReceiptPanel.defaultProps = {
  transaction: {
    id: null,
    plugins: {
      taxReceipt: [
        {
          id: null,
          status: null,
          statusId: null
        }
      ]
    }
  }
};

TaxReceiptPanel.propTypes = {
  transaction: PropTypes.object.isRequired
};

export default translate('TaxReceiptPanel')(TaxReceiptPanel);
